import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { LeadScoringConflict } from "@signalist/shared-types";

import { ScoreCriteriaType } from "../../schema/score.schema";
import { DeleteRowModal } from "../../shell/DeleteRowModal";
import { focusMatches } from "../../shell/focusTarget";
import { IssueBar } from "../../shell/IssueBar";
import { ScoreLeaveGuard } from "../../shell/ScoreLeaveGuard";
import { ScoreMatrix } from "../../shell/ScoreMatrix/ScoreMatrix";
import { ScoreSaveBar } from "../../shell/ScoreSaveBar";
import { ScoreShell } from "../../shell/ScoreShell";
import { SettingsBox } from "../../shell/SettingsBox";
import { AddChip, ChipStatus, StatusChip } from "../../shell/StatusChip";
import { PersonaView, SegmentView } from "../../shell/scoreConfigView";
import { computeScoreIssues, issuesFor, issuesForStep, ScoreIssue } from "../../shell/scoreIssues";
import { saveBlockerText } from "../../shell/saveBlocker";
import { useConfigDraft } from "../../shell/useConfigDraft";
import { useFocusTarget } from "../../shell/useFocusTarget";
import { useScoreConfig } from "../../shell/useScoreConfig";

import { ConflictCheckModal, ConflictCheckState } from "./ConflictCheckModal";
import { CriterionRef, listCriteria, setScoringActive } from "./criteriaDraft";
import { ScoringCard } from "./ScoringCard";
import { useCriteriaDraft } from "./useCriteriaDraft";
import { withPendingAssignments } from "../../shell/pendingAssignments";

const HELP_URL = "https://help.signalist.io/lead-scoring";

const CREATE_PARAMS = ["create", "segment", "persona"];

/** The criterion a conflict points at, as the editor addresses it. */
const conflictCriterionRef = (
	scoring: ScoreCriteriaType,
	conflict: LeadScoringConflict
): CriterionRef | null =>
	listCriteria(scoring).find((ref) =>
		ref.key === "OTHER"
			? ref.section === conflict.section &&
				scoring.criteria[ref.section].OTHER[ref.index ?? -1]?.criteriaName === conflict.criterion
			: ref.key === conflict.criterion
	) ?? null;

/**
 * Lead Scoring (PREC-4055): the scorings as a chip row, the selected one as a
 * card with its criteria, and the matrix that says where it applies. Grades
 * and cells save together; a changed location or industry grade runs the
 * conflict check first.
 */
export const ScoreCriteria: FunctionComponent = () => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { score, criteria, refetch } = useScoreConfig();
	const { target, clear: clearFocus } = useFocusTarget();
	const scoringsDraft = useCriteriaDraft(criteria, {
		saveError: t("leadScoring.saveScoringsError"),
	});
	const { scorings } = scoringsDraft;
	const edits = useConfigDraft(score, scorings, refetch, {
		saveError: t("leadScoring.saveError"),
		blocked: (blocker) => saveBlockerText(blocker, t),
	});

	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [selectedCriterion, setSelectedCriterion] = useState<CriterionRef | null>(null);
	const [deleting, setDeleting] = useState<ScoreCriteriaType | null>(null);
	const [check, setCheck] = useState<ConflictCheckState | null>(null);

	const selected = scorings.find((scoring) => scoring.id === selectedId) ?? scorings[0] ?? null;

	// Pin the fallback: a refetch may return the scorings in another order, and an unpinned
	// selection would follow whichever came first (PREC-4180).
	useEffect(() => {
		if (selected && selected.id !== selectedId && !scorings.some((row) => row.id === selectedId)) {
			setSelectedId(selected.id);
		}
	}, [selected, selectedId, scorings]);

	// Issues of the page as shown — pending picks included — for the selected scoring and the
	// combinations; the other scorings show theirs on their chips.
	const allIssues = useMemo(
		() =>
			issuesForStep(
				computeScoreIssues({
					score: edits.draft,
					criteria: withPendingAssignments(
						scorings,
						edits.segments,
						edits.personas,
						edits.assignmentOf
					),
				}),
				"scoring"
			),
		[edits.draft, edits.segments, edits.personas, edits.assignmentOf, scorings]
	);
	const issues = useMemo(
		() => allIssues.filter((issue) => issue.kind !== "scoring" || issue.id === `${selected?.id}`),
		[allIssues, selected?.id]
	);

	const select = (id: number) => {
		setSelectedId(id);
		setSelectedCriterion(null);
	};

	useEffect(() => {
		if (target?.kind === "scoring") {
			select(Number(target.id));
		}
	}, [target]);

	// `?create=1[&segment=&persona=]` adds a scoring and points the cell at it.
	const ready = !!edits.draft && criteria.length >= 0 && !!score;

	useEffect(() => {
		if (searchParams.get("create") !== "1" || !ready) {
			return;
		}

		const id = scoringsDraft.add(t("leadScoring.scoringPage.newName"));
		const segment = edits.segments.find((row) => row.key === searchParams.get("segment"));
		const persona = edits.personas.find((row) => `${row.id}` === searchParams.get("persona"));

		if (segment && persona) {
			edits.pick(segment, persona, id);
		}

		select(id);
		setSearchParams(
			(current) => {
				const next = new URLSearchParams(current);

				CREATE_PARAMS.forEach((param) => next.delete(param));

				return next;
			},
			{ replace: true }
		);
		// Consumed once the draft exists.
	}, [searchParams, ready]);

	/** Active combinations per scoring, counting pending picks, plus how many have none. */
	const coverage = useMemo(() => {
		const applied = new Map<number, number>();
		let live = 0;
		let missing = 0;

		for (const segment of edits.segments.filter((row) => row.active)) {
			for (const persona of edits.personas.filter((row) => row.active)) {
				if (!edits.isLinked(segment, persona)) {
					continue;
				}

				live += 1;

				const assignment = edits.assignmentOf(segment, persona);

				if (assignment) {
					applied.set(assignment.criteriaId, (applied.get(assignment.criteriaId) ?? 0) + 1);
				} else {
					missing += 1;
				}
			}
		}

		return { applied, live, missing };
	}, [edits]);

	const statusOf = (scoring: ScoreCriteriaType): ChipStatus =>
		!scoring.locked
			? "off"
			: issuesFor(allIssues, "scoring", `${scoring.id}`).length > 0
				? "bad"
				: "ok";

	const addScoring = (segment?: SegmentView, persona?: PersonaView) => {
		const id = scoringsDraft.add(t("leadScoring.scoringPage.newName"));

		if (segment && persona) {
			edits.pick(segment, persona, id);
		}

		select(id);
	};

	const dirty = scoringsDraft.dirty || edits.dirty;
	const saving = scoringsDraft.saving || edits.saving;

	/** Scorings first so the matrix can translate temporary ids, then the cells. */
	const persist = useCallback(async (): Promise<boolean> => {
		const idMap = await scoringsDraft.save();

		if (!idMap) {
			return false;
		}

		const ok = await edits.save((criteriaId) => idMap.get(criteriaId) ?? criteriaId);

		scoringsDraft.settle();
		setSelectedId((current) => (current === null ? current : (idMap.get(current) ?? current)));

		return ok;
	}, [scoringsDraft, edits]);

	const saveWithCheck = useCallback(async (): Promise<boolean> => {
		if (!scoringsDraft.needsConflictCheck()) {
			return persist();
		}

		setCheck({ phase: "checking" });

		try {
			const outcome = await scoringsDraft.checkConflicts();

			if (outcome.scorings.length > 0) {
				setCheck({ phase: "found", outcome });

				return false;
			}
		} catch {
			setCheck({ phase: "failed" });

			return false;
		}

		const ok = await persist();

		setCheck(ok ? { phase: "none" } : null);

		return ok;
	}, [scoringsDraft, persist]);

	const saveAnyway = async () => {
		setCheck({ phase: "checking" });

		const ok = await persist();

		setCheck(ok ? { phase: "none" } : null);
	};

	const discard = () => {
		scoringsDraft.discard();
		edits.discard();
	};

	const fixConflict = (scoringId: number, conflict: LeadScoringConflict) => {
		const scoring = scorings.find((row) => row.id === scoringId);
		const ref = scoring ? conflictCriterionRef(scoring, conflict) : null;

		setSelectedId(scoringId);
		setSelectedCriterion(ref);
		setCheck(null);
	};

	const selectIssue = (issue: ScoreIssue) => {
		if (issue.kind === "scoring") {
			select(Number(issue.id));
		}
	};

	return (
		<ScoreShell helpUrl={HELP_URL} intro={t("leadScoring.scoringPage.intro")} step="scoring">
			<IssueBar
				issues={issues}
				subject={t("leadScoring.scoringPage.subject")}
				onSelect={selectIssue}
			/>

			<div
				className="sticky top-[88px] z-30 -mx-1 mb-3 flex flex-wrap items-center gap-2 bg-white px-1 py-2"
				data-testid="scoring-chips"
			>
				{scorings.map((scoring) => (
					<StatusChip
						key={scoring.id}
						label={scoring.name || t("leadScoring.shell.untitled")}
						selected={scoring.id === selected?.id}
						status={statusOf(scoring)}
						testId={`scoring-chip-${scoring.id}`}
						onClick={() => select(scoring.id)}
					/>
				))}
				<AddChip
					label={t("leadScoring.scoringPage.add")}
					testId="scoring-add"
					onClick={() => addScoring()}
				/>
			</div>

			{selected ? (
				<ScoringCard
					key={selected.id}
					applied={coverage.applied.get(selected.id) ?? 0}
					flash={focusMatches(target, "scoring", `${selected.id}`)}
					others={scorings.filter((scoring) => scoring.id !== selected.id)}
					scoring={selected}
					selectedCriterion={selectedCriterion}
					onChange={(next) => scoringsDraft.update(selected.id, () => next)}
					onDelete={() => setDeleting(selected)}
					onDuplicate={() =>
						select(
							scoringsDraft.duplicate(
								selected.id,
								t("leadScoring.scoringPage.copyName", { name: selected.name })
							)
						)
					}
					onFlashDone={clearFocus}
					onSelectCriterion={setSelectedCriterion}
				/>
			) : (
				<p className="text-sm text-text-meta" data-testid="scoring-none">
					{t("leadScoring.scoringPage.none")}
				</p>
			)}

			{selected && (
				<SettingsBox
					className="mt-5"
					testId="scoring-applies"
					title={t("leadScoring.scoringPage.appliesTitle", { name: selected.name })}
				>
					<ScoreMatrix
						assignmentOf={edits.assignmentOf}
						criteria={scorings}
						focusTarget={target}
						isLinked={edits.isLinked}
						personas={edits.personas}
						segments={edits.segments}
						onClear={edits.clear}
						onCreate={addScoring}
						onDeactivate={(segment, persona) => edits.setLinked(segment, persona, false)}
						onEdit={select}
						onFocusDone={clearFocus}
						onPick={edits.pick}
						onRename={edits.rename}
					/>
					<div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-text-meta">
						<b className="font-semibold text-text" data-testid="scoring-applied-count">
							{t("leadScoring.scoringPage.appliesCount", {
								count: coverage.applied.get(selected.id) ?? 0,
							})}
						</b>
						<span>·</span>
						<span>
							{t("leadScoring.scoringPage.stillNone", {
								missing: coverage.missing,
								live: coverage.live,
							})}
						</span>
					</div>
				</SettingsBox>
			)}

			{deleting && (
				<DeleteRowModal
					body={
						(coverage.applied.get(deleting.id) ?? 0) > 0
							? t("leadScoring.scoringPage.deleteBodyApplied", {
									count: coverage.applied.get(deleting.id),
								})
							: t("leadScoring.scoringPage.deleteBodyUnused")
					}
					title={t("leadScoring.scoringPage.deleteTitle", {
						name: deleting.name || t("leadScoring.shell.untitled"),
					})}
					onClose={() => setDeleting(null)}
					onDeactivate={() => {
						scoringsDraft.update(deleting.id, (scoring) => setScoringActive(scoring, false));
						setDeleting(null);
					}}
					onDelete={() => {
						scoringsDraft.remove(deleting.id);
						setSelectedId(null);
						setDeleting(null);
					}}
				/>
			)}

			{check && (
				<ConflictCheckModal
					scoringName={(id) => scorings.find((scoring) => scoring.id === id)?.name ?? ""}
					state={check}
					onClose={() => setCheck(null)}
					onFix={fixConflict}
					onSaveAnyway={saveAnyway}
				/>
			)}

			<ScoreSaveBar
				dirty={dirty}
				error={scoringsDraft.error ?? edits.error}
				saving={saving}
				onDiscard={discard}
				onSave={saveWithCheck}
			/>
			<ScoreLeaveGuard when={dirty} onDiscard={discard} onSave={saveWithCheck} />
		</ScoreShell>
	);
};
